import { cn } from '@pesto/utils';
import { ChevronRightIcon } from '@radix-ui/react-icons';
import { useState } from 'react';
import { useLocation } from 'react-router-dom';

import type { ExpandableMenuItem, ExpandableMenuList } from './Navigation.types';

import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../Collapsible';
import { Popover, PopoverContent, PopoverTrigger } from '../Popover';
import { Typography } from '../Typography';
import { NavigationLink } from './NavigationLink';

const getHrefPathname = (href?: string) => href?.split('?')[0];

const isCurrentMenuItem = (item: ExpandableMenuItem, pathname: string): boolean => {
  if (item.type === 'item') return getHrefPathname(item.href) === pathname;

  if (item.type === 'sub') return item.items.some((subItem) => isCurrentMenuItem(subItem, pathname));

  return false;
};

export interface ExpandableNavProps {
  triggerClassName?: string;
  links: ExpandableMenuList;
  minimized?: boolean;
  onLinkClick?: (href: string) => void;
}

export const ExpandableNav = (props: ExpandableNavProps) => {
  const { links, minimized, onLinkClick, triggerClassName } = props;
  const location = useLocation();

  const [openItems, setOpenItems] = useState<Set<number>>(
    () => new Set(links.flatMap((item, index) => (isCurrentMenuItem(item, location.pathname) ? [index] : []))),
  );

  const handleLinkClick = (href: string) => {
    onLinkClick?.(href);
  };

  const renderMenuItems = (items: ExpandableMenuItem[], isSub?: boolean) => {
    return items.map((item, index) => {
      if (item.type === 'label') {
        return (
          <Typography variant={'bodyLarge'} key={index}>
            {item.label}
          </Typography>
        );
      }

      if (item.type === 'separator') {
        return <hr key={index} />;
      }

      if (item.type === 'custom') {
        return <div key={index}>{item.component}</div>;
      }

      if (item.type === 'item') {
        const current = getHrefPathname(item.href) === location.pathname;

        return (
          <NavigationLink
            key={index}
            {...item}
            current={current}
            minimized={minimized}
            isSub={isSub}
            onClick={handleLinkClick}
            className={triggerClassName}
          />
        );
      }

      if (item.type === 'sub') {
        const Icon = item.icon;
        const current = isCurrentMenuItem(item, location.pathname);
        const open = openItems.has(index);

        const RootComponent = !minimized ? Collapsible : Popover;
        const TriggerComponent = !minimized ? CollapsibleTrigger : PopoverTrigger;
        const ContentComponent = !minimized ? CollapsibleContent : PopoverContent;

        return (
          <RootComponent
            key={index}
            className="group/collapsible w-full"
            open={open}
            onOpenChange={(isOpen: boolean) => {
              const next = new Set(openItems);

              if (isOpen) next.add(index);
              else next.delete(index);
              setOpenItems(next);
            }}
          >
            <TriggerComponent
              className={cn(
                'hover:bg-sidebar-text-selected/10 hover:text-sidebar-text-hover focus-visible:ring-sidebar-text-selected/30 text-sidebar-text flex h-8 w-full items-center gap-2 overflow-hidden rounded-md px-2 text-left text-sm outline-none transition-colors focus-visible:ring-2',
                minimized && 'mx-auto w-8 justify-center px-0',
                current && 'bg-sidebar-text-selected/10 text-sidebar-text-selected-foreground font-medium',
              )}
            >
              {Icon && <Icon className="h-4 w-4 shrink-0" />}

              {!minimized && (
                <>
                  <span className="min-w-0 flex-1 truncate">{item.label}</span>
                  <ChevronRightIcon
                    id="chevron"
                    className="ml-auto h-4 w-4 shrink-0 transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90"
                  />
                </>
              )}
            </TriggerComponent>
            <ContentComponent
              className={cn(
                'data-[state=closed]:hidden',
                minimized
                  ? 'bg-sidebar border-sidebar-border/30 flex w-56 flex-col gap-0.5 rounded-lg border p-1 shadow-xl'
                  : 'border-sidebar-border/40 mx-3.5 flex min-w-0 translate-x-px flex-col gap-0.5 border-l px-2.5 py-0.5',
              )}
              onMouseLeave={() => {
                if (minimized) {
                  const next = new Set(openItems);
                  next.delete(index);
                  setOpenItems(next);
                }
              }}
            >
              {renderMenuItems(item.items, true)}
            </ContentComponent>
          </RootComponent>
        );
      }

      return null;
    });
  };

  return <div className="flex flex-col gap-1">{renderMenuItems(links)}</div>;
};
